import { useState, useRef, useCallback } from 'react';
import { theme } from '../../theme';

function fmt(s) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function MessageInput({ onSend, disabled, token }) {
  const [text, setText] = useState('');
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const taRef = useRef(null);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  // Ajusta la altura del textarea a su contenido (auto-crecimiento) hasta un máximo,
  // a partir del cual aparece scroll. Se llama al escribir, pegar y al limpiar.
  const autoResize = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`;
  };

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend({ content: trimmed, type: 'text' });
    setText('');
    if (taRef.current) taRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Sube un archivo y devuelve su URL pública (WhatsApp no puede descargar data
  // URLs/blobs locales: hay que subirlo primero y enviar la URL).
  const uploadTo = useCallback(async (endpoint, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    return data.url || null;
  }, [token]);

  const sendFile = useCallback(async (file, type, content, endpoint = '/api/agent/archives/upload') => {
    setUploading(true);
    try {
      const url = await uploadTo(endpoint, file);
      if (!url) { alert('No se pudo subir el archivo'); return; }
      onSend({ content: content ?? null, type, media_url: url });
    } catch {
      alert('Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  }, [uploadTo, onSend]);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) sendFile(file, 'image', null);
      }
    }
  }, [sendFile]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const type = file.type.startsWith('image/') ? 'image' : 'file';
    sendFile(file, type, file.name);
  };

  const stopStream = () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setRecording(false);
    setSeconds(0);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      cancelledRef.current = false;
      recorder.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const cancelled = cancelledRef.current;
        stopStream();
        if (cancelled) return;
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (!blob.size) return;
        const file = new File([blob], 'nota-voz.webm', { type: 'audio/webm' });
        await sendFile(file, 'audio', null, '/api/agent/audio/upload');
      };
      recorder.start();
      mediaRef.current = recorder;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      alert('No se pudo acceder al micrófono');
    }
  };

  const finishAndSend = () => {
    cancelledRef.current = false;
    mediaRef.current?.stop(); // dispara onstop → sube y envía
  };

  const cancelRecording = () => {
    cancelledRef.current = true;
    mediaRef.current?.stop(); // dispara onstop → descarta
  };

  const btnSt = {
    background: 'transparent', border: 'none', cursor: 'pointer',
    fontSize: '1.2rem', padding: '0 6px', color: theme.colors.textMuted,
    flexShrink: 0,
  };

  const sendBtnSt = (enabled) => ({
    background: theme.colors.primary, border: 'none', borderRadius: '50%',
    width: '38px', height: '38px', cursor: enabled ? 'pointer' : 'not-allowed', fontSize: '1rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    opacity: enabled ? 1 : 0.5,
  });

  // Mientras graba, mostramos una barra clara con cronómetro y botones de
  // cancelar / enviar (estilo nota de voz de WhatsApp).
  if (recording) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '10px 14px', borderTop: `1px solid ${theme.colors.border}`,
        background: theme.colors.surface,
      }}>
        <button style={{ ...btnSt, color: theme.colors.error }} title="Cancelar" onClick={cancelRecording}>🗑️</button>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.text }}>
          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: theme.colors.error, display: 'inline-block' }} />
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>Grabando… {fmt(seconds)}</span>
        </div>
        <button style={sendBtnSt(true)} title="Enviar nota de voz" onClick={finishAndSend}>➤</button>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', gap: '8px',
      padding: '10px 14px', borderTop: `1px solid ${theme.colors.border}`,
      background: theme.colors.surface,
    }}>
      <input ref={fileRef} type="file" hidden onChange={handleFile} />
      <button
        style={{ ...btnSt, opacity: uploading ? 0.4 : 1, cursor: uploading ? 'wait' : 'pointer' }}
        title={uploading ? 'Subiendo…' : 'Adjuntar archivo'}
        disabled={uploading}
        onClick={() => fileRef.current?.click()}
      >
        📎
      </button>
      <button
        style={{ ...btnSt, opacity: uploading ? 0.4 : 1 }}
        title="Grabar nota de voz"
        disabled={uploading}
        onClick={startRecording}
      >
        🎤
      </button>
      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => { setText(e.target.value); autoResize(); }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        disabled={disabled}
        placeholder={uploading ? 'Enviando archivo…' : 'Escribe un mensaje...'}
        rows={1}
        style={{
          flex: 1, resize: 'vertical', padding: '8px 12px', borderRadius: '20px',
          background: theme.colors.background, border: `1px solid ${theme.colors.border}`,
          color: theme.colors.text, outline: 'none', fontSize: '0.9rem',
          fontFamily: 'inherit', minHeight: '38px', maxHeight: '260px', overflowY: 'auto',
        }}
      />
      <button onClick={handleSend} disabled={disabled || !text.trim()} style={sendBtnSt(!!text.trim() && !disabled)}>
        ➤
      </button>
    </div>
  );
}
